// "Can't find it? Add a skill" under a skill picker (Q79). The new skill is
// usable at once by whoever adds it and reviewed by a curator after. Not a
// <form>: it sits inside the role form and the profile wizard. Close
// existing names are offered first, so "ReactJS" picks React instead of
// sending a near-duplicate to review.
import { similarNames, type SkillStatus } from '@remote-medium/shared';
import { cn } from 'cn';
import { PlusIcon } from 'lucide-react';
import { useState } from 'react';

import { isFieldError } from './api/errors.ts';
import { notifyError } from './api/notify.ts';
import { badgeVariants } from './components/badge.tsx';
import { Button } from './components/button.tsx';
import { Input } from './components/input.tsx';

export interface AddedSkill {
  id: string;
  name: string;
  category: string | null;
  families: string[];
  industries: string[];
  roleTypes: string[];
  status: SkillStatus;
}

interface Props {
  onAdd: (name: string) => Promise<AddedSkill>;
  /** Called with the created (or existing, same-name) skill so the caller can pick it. */
  onAdded: (skill: AddedSkill) => void;
  /** What the person typed in the search box, offered as the starting name. */
  suggestedName?: string;
  /** Skills that can still be picked; close names are offered before adding. */
  existing: readonly AddedSkill[];
}

export function AddSkill({ onAdd, onAdded, suggestedName = '', existing }: Props) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The typed name turned out to be a listed skill, which was picked instead.
  const [alreadyListed, setAlreadyListed] = useState<string | null>(null);

  if (!open) {
    const opener = (
      <button
        type="button"
        onClick={() => {
          setName(suggestedName.trim());
          setAlreadyListed(null);
          setOpen(true);
        }}
        className="self-start text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
      >
        Can&apos;t find it? Add a skill
      </button>
    );
    if (!alreadyListed) return opener;
    return (
      <div className="flex flex-col gap-1">
        <p role="status" className="text-xs text-muted-foreground">
          &ldquo;{alreadyListed}&rdquo; is already listed, so we picked it.
        </p>
        {opener}
      </div>
    );
  }

  const matches = similarNames(name, existing);

  // Selects `skill` and closes the form. `typed`: added by name rather than
  // picked from the suggestions, so say so when the name was already listed.
  function done(skill: AddedSkill, typed: boolean) {
    onAdded(skill);
    // A new skill is always pending; approved means the name was taken.
    setAlreadyListed(typed && skill.status === 'approved' ? skill.name : null);
    setOpen(false);
    setName('');
  }

  async function add(skillName: string) {
    if (skillName.length < 2) {
      setError('Add a skill name.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      done(await onAdd(skillName), true);
    } catch (e) {
      // Only refusals of the name show here; session and server errors toast.
      if (isFieldError(e)) setError(e.message);
      else notifyError(e, 'Could not add the skill. Try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-border bg-muted/40 p-3">
      <span className="text-xs text-muted-foreground">
        You can use it straight away. A curator reviews every new skill and may switch it to a close
        existing one; we email you if that happens.
      </span>
      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={60}
          placeholder="e.g. Revenue forecasting"
          aria-label="New skill name"
          className="max-w-72"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void add(name.trim());
            }
          }}
        />
        <Button type="button" size="sm" disabled={busy} onClick={() => void add(name.trim())}>
          <PlusIcon />
          {busy ? 'Adding…' : matches.length ? 'Add as a new skill' : 'Add and use it'}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </div>
      {matches.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <span className="text-xs text-muted-foreground">Already listed. Use one of these?</span>
          <div className="flex flex-wrap gap-1.5">
            {matches.map((m) => (
              <button
                key={m.id}
                type="button"
                disabled={busy}
                onClick={() => done(m, false)}
                className={cn(badgeVariants({ variant: 'outline' }), 'cursor-pointer')}
              >
                {m.name}
              </button>
            ))}
          </div>
        </div>
      )}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
